'use client';

import React, { KeyboardEventHandler, useState } from 'react';
import styles from './GeoLocationInput.module.scss';
import Text from '@JohnLewisPartnership/jlfs-ui-library/dist/components/inputs/text/text';
import Icon from '@JohnLewisPartnership/jlfs-ui-library/dist/components/icon/icon';
import Modal from '@JohnLewisPartnership/jlfs-ui-library/dist/components/Modal/Modal';
import Select from '@JohnLewisPartnership/jlfs-ui-library/dist/components/inputs/select/select';
import { PrimaryButton } from '@JohnLewisPartnership/jl-design-system/dist/elements/button/Button';
import { LoadingSpinner } from '@JohnLewisPartnership/jl-design-system/dist/components/loading-spinner/LoadingSpinner';
import { TServiceOption } from '../../wrappers/StoreLocatorWrapper/StoreLocatorWrapper';
import classNames from 'classnames';
import { dispatchAnalyticsEvent } from '@JohnLewisPartnership/jlfs-ui-library/dist/components/analytics/Analytics';
import { trackMonetateEvent } from '@JohnLewisPartnership/jlfs-ui-library/dist/utils/monetate/trackMonetateEvent';
import { getCoordinates } from '../../../actions/server/getCoordinates';
import { GeocodeStatus } from '../../../actions/server/getCoordinates.constants';
import {
  CLICK_AND_COLLECT,
  BUY_CURRENCY_IN_STORE,
  ALL_TRAVEL_MONEY_STORES,
} from '../../../constants/listOptions';

export default function GeoLocationInput({
  onCoordinates,
  onServiceChange,
  serviceInformationModal,
  defaultServiceOption,
  monetateTrackingId,
}: {
  onCoordinates?: (coords: {
    lat: number;
    lng: number;
    address?: string;
  }) => void;
  serviceInformationModal?: {
    title: string;
    content: React.ReactNode;
    primaryCta?: {
      title: string;
      newTab?: boolean;
      url: string;
    };
    secondaryCta?: {
      title: string;
      newTab?: boolean;
      url: string;
    };
  };
  onServiceChange?: (service: TServiceOption) => void;
  defaultServiceOption?: TServiceOption;
  monetateTrackingId?: string;
}) {
  const [address, setAddress] = useState('');
  const [loadingMyLocation, setLoadingMyLocation] = useState(false);
  const [loadingAddressSearch, setLoadingAddressSearch] = useState(false);
  const [error, setError] = useState('');
  const [showServiceModal, setShowServiceModal] = useState(false);

  const dispatchSearchEvent = (postCode: string): void => {
    dispatchAnalyticsEvent('storeLocatorSearch', {
      searchLocation: postCode.slice(0, 4).trim() ?? '',
    });
  };

  const dispatchLocationEvent = (): void => {
    dispatchAnalyticsEvent('storeLocatorSearch', {
      searchLocation: 'current location',
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAddress(e.target.value);
    setError('');
  };

  const handleUseMyLocation = () => {
    setLoadingMyLocation(true);
    setError('');
    dispatchLocationEvent();
    if (!navigator.geolocation) {
      setError('Geolocation is not supported.');
      setLoadingMyLocation(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        const locationData = { lat: latitude, lng: longitude };
        onCoordinates && onCoordinates(locationData);

        // Cache the location in localStorage
        localStorage.setItem(
          'lastLocation',
          JSON.stringify({ ...locationData, address: 'Current Location' })
        );

        setLoadingMyLocation(false);
      },
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      (err) => {
        setError('Unable to retrieve your location.');
        setLoadingMyLocation(false);
      },
      {
        enableHighAccuracy: false,
      }
    );
  };

  const handleAddressSearch = async () => {
    if (!address) {
      setError('Please enter an address.');
      return;
    }
    trackMonetateEvent(monetateTrackingId);
    dispatchSearchEvent(address);
    setLoadingAddressSearch(true);
    setError('');

    const { results, status } = await getCoordinates(address);
    if (status === GeocodeStatus.Ok && results.length > 0) {
      const { lat, lng } = results[0].geometry.location;
      onCoordinates && onCoordinates({ lat, lng, address });
    } else if (status === GeocodeStatus.ZeroResults) {
      setError('Address not found.');
    } else {
      setError('Error fetching location.');
    }
    setLoadingAddressSearch(false);
  };

  const handleServiceChange = (selectedOption: TServiceOption) => {
    onServiceChange && onServiceChange(selectedOption);
  };

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (event.key === 'Enter') {
      handleAddressSearch();
    }
  };

  const locationIcon = (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 21C11.6906 20.6906 11.3402 20.3244 10.9652 19.9135C8.51122 17.2251 5 12.6227 5 9.5C5 5 9 3 12 3C15 3 19 5 19 9.5C19 12.6227 15.4888 17.2251 13.0348 19.9135C12.6598 20.3244 12.3094 20.6906 12 21ZM15.1367 14.8785C14.1345 16.3446 12.9825 17.7394 12 18.822C11.0175 17.7394 9.86554 16.3446 8.86332 14.8785C8.16587 13.8582 7.56142 12.8331 7.13596 11.8774C6.70273 10.9043 6.5 10.1022 6.5 9.5C6.5 7.75511 7.25143 6.55364 8.29217 5.74943C9.37831 4.91014 10.7972 4.5 12 4.5C13.2028 4.5 14.6217 4.91014 15.7078 5.74943C16.7486 6.55364 17.5 7.75511 17.5 9.5C17.5 10.1022 17.2973 10.9043 16.864 11.8774C16.4386 12.8331 15.8341 13.8582 15.1367 14.8785ZM10.5 10C10.5 9.17157 11.1716 8.5 12 8.5C12.8284 8.5 13.5 9.17157 13.5 10C13.5 10.8284 12.8284 11.5 12 11.5C11.1716 11.5 10.5 10.8284 10.5 10ZM12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7Z"
        fill={loadingAddressSearch ? '#979797' : '#141414'}
      />
    </svg>
  );

  return (
    <div className={styles.wrapper}>
      <div className={styles.heading}>Find a travel money store</div>
      <p>
        Enter your{' '}
        <span id="addressInputLabel">postcode, town or street name</span>
      </p>
      <Text
        aria-labelledby="addressInputLabel"
        name="addressInput"
        value={address}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        disabled={loadingAddressSearch || loadingMyLocation}
      />
      <button
        onClick={handleUseMyLocation}
        disabled={loadingAddressSearch}
        className={classNames(
          styles.button,
          loadingMyLocation ? styles.buttonDisabled : null
        )}
      >
        {/* <Icon name="pin" size="small" style="outlined" /> */}
        {loadingMyLocation ? (
          <div className={styles.loadingContainer}>
            <LoadingSpinner className={styles.loadingSpinner} />
          </div>
        ) : (
          <>
            {locationIcon}
            Use current location
          </>
        )}
      </button>
      <p id="serviceSelectLabel">Select travel money service</p>
      <Select
        aria-labelledby="serviceSelectLabel"
        name="serviceSelect"
        data-testid="service-select"
        disabled={loadingAddressSearch || loadingMyLocation}
        options={[
          { value: CLICK_AND_COLLECT, label: CLICK_AND_COLLECT },
          {
            value: BUY_CURRENCY_IN_STORE,
            label: BUY_CURRENCY_IN_STORE,
          },
          {
            value: ALL_TRAVEL_MONEY_STORES,
            label: ALL_TRAVEL_MONEY_STORES,
          },
        ]}
        defaultValue={defaultServiceOption}
        onChange={(e) => handleServiceChange(e.target.value as TServiceOption)}
      />
      {serviceInformationModal && (
        <>
          <button
            className={styles.button}
            onClick={() => setShowServiceModal(true)}
            disabled={loadingAddressSearch || loadingMyLocation}
          >
            <Icon name="help" size="medium" />
            What can I do with each service?
          </button>
          <Modal
            title={serviceInformationModal.title}
            isOpen={showServiceModal}
            onClose={() => setShowServiceModal(false)}
            primaryCta={
              serviceInformationModal.primaryCta
                ? {
                    label: serviceInformationModal.primaryCta.title,
                    newTab: serviceInformationModal.primaryCta.newTab || false,
                    url: serviceInformationModal.primaryCta.url,
                  }
                : undefined
            }
            secondaryCta={
              serviceInformationModal.secondaryCta
                ? {
                    label: serviceInformationModal.secondaryCta.title,
                    newTab:
                      serviceInformationModal.secondaryCta.newTab || false,
                    url: serviceInformationModal.secondaryCta.url,
                  }
                : undefined
            }
          >
            {/* @ts-ignore */}
            {serviceInformationModal.content}
          </Modal>
        </>
      )}
      <PrimaryButton
        onClick={handleAddressSearch}
        submitting={loadingAddressSearch}
        disabled={loadingMyLocation}
      >
        Search Stores
      </PrimaryButton>
      {error && <div className={styles.error}>{error}</div>}
    </div>
  );
}
